/**
 * Готовность комплекта и принятые ограничения подбора (ретест 17.09.2026, R01/R02/R03/R11).
 *
 * Чистые функции и типы контракта — отдельным модулем, чтобы страница подбора, конструктор и
 * тесты говорили об одном и том же одними и теми же словами.
 */

export type ReadinessStatus = 'READY' | 'NEEDS_MORE' | 'UNKNOWN';

export interface RoleGap {
    role: string;
    label: string;
    reason: string;
}

export interface Readiness {
    status: ReadinessStatus;
    statusLabel?: string | null;
    gaps?: RoleGap[] | null;
    missing?: string[] | null;
    satisfied?: string[] | null;
    unchecked?: string[] | null;
}

export interface AppliedConstraints {
    occasion?: string | null;
    occasionLabel?: string | null;
    manualTempC?: number | null;
    manualTempLabel?: string | null;
    manualPrecipitation?: boolean | null;
    forecastTempC?: number | null;
    removableOuterIndoors?: boolean;
    closedFootwearRequired?: boolean;
    recognized?: string[] | null;
}

/** Вид улучшения: добавить недостающую роль либо заменить вещь внутри роли. */
export type ImprovementKind = 'ADD_ROLE' | 'REPLACE_IN_ROLE';

/** Подписи статуса готовности; бэкенд присылает свою, это запасной вариант. */
const STATUS_LABELS: Record<ReadinessStatus, string> = {
    READY: 'Комплект полный',
    NEEDS_MORE: 'Нужно дополнить',
    UNKNOWN: 'Полноту проверить не удалось',
};

export function readinessLabel(readiness: Readiness | null | undefined): string {
    if (!readiness) return STATUS_LABELS.UNKNOWN;
    return readiness.statusLabel || STATUS_LABELS[readiness.status] || STATUS_LABELS.UNKNOWN;
}

/** Готов ли комплект к выходу: только явный READY, неизвестность обещанием не считается. */
export function isReady(readiness: Readiness | null | undefined): boolean {
    return readiness?.status === 'READY';
}

/** Строка «Не хватает: …» — подписи недостающих ролей. */
export function missingLabel(readiness: Readiness | null | undefined): string {
    const missing = readiness?.missing ?? (readiness?.gaps ?? []).map(g => g.label);
    return missing.join(', ');
}

/**
 * Резюме ограничений перед запуском подбора (FR-2.5). Попадает только то, что продукт
 * действительно учтёт: обратного ожидания интерфейс не создаёт.
 */
export function constraintsSummary(params: {
    occasionLabel?: string | null;
    tempC?: number | null;
    precipitation?: boolean | null;
}): string[] {
    const out: string[] = [];
    if (params.occasionLabel) out.push(`Повод: ${params.occasionLabel}`);
    if (params.tempC != null && Number.isFinite(params.tempC)) out.push(`Температура: ${formatTemp(params.tempC)} °C`);
    if (params.precipitation === true) out.push('Осадки: да');
    if (params.precipitation === false) out.push('Осадки: нет');
    return out;
}

/** «+10» / «-5» / «0» — со знаком, без дробной части. */
export function formatTemp(tempC: number): string {
    const rounded = Math.round(tempC);
    return rounded > 0 ? `+${rounded}` : String(rounded);
}

/**
 * Технические причины подбора, которым не место в основном потоке (R11): коды поводов,
 * номера вариантов, уровни автономии и служебные пометки добора.
 */
const TECHNICAL_REASON_PREFIXES = [
    'Повод:', 'Вариант ', 'Уровень автономии', 'Стратегия:', 'Архетип', 'Добрано без учёта повода',
];

export function isTechnicalReason(reason: string): boolean {
    return TECHNICAL_REASON_PREFIXES.some(prefix => reason.startsWith(prefix));
}

/** Разделение причин на человеческие и технические (последние уходят в раскрываемые детали). */
export function splitReasons(reasons: string[]): {human: string[]; technical: string[]} {
    const human: string[] = [];
    const technical: string[] = [];
    reasons.forEach(reason => (isTechnicalReason(reason) ? technical : human).push(reason));
    return {human, technical};
}

/**
 * Готовность из старого контракта (`complete` + `missingSlots`) — для ответов, где блока
 * `readiness` ещё нет. Без этого свежий интерфейс на старом ответе показывал бы «Полноту
 * проверить не удалось» там, где полнота как раз известна.
 */
export function fallbackReadiness(complete: boolean | null | undefined,
                                  missing: string[] | null | undefined): Readiness | null {
    if (complete == null) return null;
    const labels = (missing ?? []).filter(Boolean);
    if (complete) {
        return {status: 'READY', missing: [], gaps: [], satisfied: [], unchecked: []};
    }
    return {
        status: 'NEEDS_MORE',
        missing: labels,
        gaps: labels.map(label => ({role: label, label, reason: 'В комплекте нет этой вещи'})),
        satisfied: [],
        unchecked: [],
    };
}
